import { AlertChannel, AlertDigestMode, AlertSeverity, AlertType, enumKeys, getAlertDigestModeDisplayName, getAlertTypeDisplayName } from "@app/shared";
import { formatInTimeZone } from "date-fns-tz";
import { SelectOption } from "../../shared/inputs";

// Fallback wording for enums with no [Display(Name = "...")] on the C# side — AlertSeverity,
// AlertStatus, AlertChannel, AlertWebhookFormat. Derives "DeviceOffline" -> "Device Offline" from the
// member name.
//
// Prefer the generated get<Enum>DisplayName() helpers where they exist (AlertType, AlertDigestMode):
// the exporter emits the real Display text, so those read "High RAM Usage" rather than this function's
// "High Ram Usage", and a wording change on the server reaches the UI without touching this file.
export const enumLabel = (key: string): string => key.replace(/_/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2");

// Display text for an alert type, straight from the C# [Display(Name)] values.
export const alertTypeLabel = (type?: AlertType): string =>
    type === undefined ? "" : getAlertTypeDisplayName(type) ?? enumLabel(AlertType[type] ?? "");

// Display text for a delivery mode ("Daily Digest", not "Daily Digest" reconstructed by regex).
export const digestModeLabel = (mode?: AlertDigestMode): string =>
    mode === undefined ? "" : getAlertDigestModeDisplayName(mode) ?? enumLabel(AlertDigestMode[mode] ?? "");

// "Minimum severity" is a threshold, not the severity of one alert, so it reads
// differently. Shared by the tables and their editors so a row and the dialog that
// edits it can never word the same value two ways.
export const minSeverityOptions: SelectOption[] = [
    { value: AlertSeverity.Info, label: "Info and up" },
    { value: AlertSeverity.Warning, label: "Warning and up" },
    { value: AlertSeverity.Critical, label: "Critical only" },
];

export const minSeverityLabel = (severity?: AlertSeverity): string => minSeverityOptions.find((o) => o.value === severity)?.label ?? "";

export const severityLabel = (severity?: AlertSeverity): string => (severity === undefined ? "" : enumLabel(AlertSeverity[severity] ?? ""));

// StatusDot's vocabulary, so an alert's severity reads the same here as device/endpoint
// health does elsewhere in the dashboard.
export const severityDotStatus = (severity?: AlertSeverity): "ok" | "warn" | "error" | undefined =>
    severity === AlertSeverity.Critical ? "error" : severity === AlertSeverity.Warning ? "warn" : severity === AlertSeverity.Info ? "ok" : undefined;

// Channels is a [Flags] enum, so a subscription can name several at once.
export const channelsLabel = (channels?: AlertChannel | number): string => {
    const names = enumKeys(AlertChannel)
        .filter((k) => (AlertChannel[k] as number) !== 0 && ((channels ?? 0) & (AlertChannel[k] as number)) !== 0)
        .map((k) => enumLabel(k));
    return names.length ? names.join(", ") : "None";
};

export const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

// Never render a bare clock time: the zone abbreviation is always spelled out, so a
// timestamp can't be misread by someone sitting in a different zone.
//
// The zone is a parameter with the viewer's own as the default, rather than always the
// viewer's, because the two kinds of caller genuinely differ: alert rows carry their
// location's zone and pass it, so each row reads in the zone the device is actually in;
// the notification-settings rows are not tied to a location at all and take the default.
export const zonedTime = (value?: Date | string | number | null, zone: string = browserTimeZone()): string => {
    if (value === null || value === undefined || value === "") return "—";
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return "—";
    try {
        return formatInTimeZone(date, zone, "MM/dd/yyyy p zzz");
    } catch {
        // Unknown/invalid IANA zone — still show the instant rather than nothing.
        return date.toISOString();
    }
};
